import React from 'react'
import {
  AlertTriangle,
  ArrowLeft,
  Edit,
  Link,
  MapPin,
  MessageSquare,
  MoreVertical,
} from 'react-feather'
import {
  FormattedDate,
  FormattedMessage,
  FormattedTime,
  useIntl,
} from 'react-intl'
import { toast } from 'react-toastify'
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Modal,
  ModalBody,
  UncontrolledButtonDropdown,
} from 'reactstrap'
import styled from 'styled-components'
import { queries } from '~/constants/mediaQuery'
import Textarea from './Textarea'
import useAxios from 'axios-hooks'
import { VariablesSelect as Select } from '../pages/app/TextTemplates'

const REVIEW_TRANSLATION_REGEX = /\(Translated by Google\) (.|\n)*\n\n\(Original\)\n|\n\n\(Translated by Google\)\n(.|\n)*/

const QuestionCard = ({ question, fetchQuestions, templates }) => {
  const intl = useIntl()
  const [addressShow, setAddressShow] = React.useState(false)
  const [reply, setReply] = React.useState('')
  const [isFocused, setIsFocused] = React.useState(false)
  const [modal, setModal] = React.useState(false)
  const [isEditing, setIsEditing] = React.useState(false)
  const [notFoundModal, setNotFoundModal] = React.useState(false)
  const [merchantAnswer, setMerchantAnswer] = React.useState({})
  const [userAnswers, setUserAnswers] = React.useState([])
  const textareaRef = React.useRef(null)
  const variableOptions = (templates || []).map(t => ({
    label: t.name,
    value: t.content,
  }))

  const [{ loading }, postReply] = useAxios(
    {
      method: 'PUT',
      url: `/questions/${encodeURIComponent(question.id)}/reply`,
    },
    { manual: true }
  )

  function handleCopyToClipboard(event) {
    event.preventDefault()
    const url = new URL(
      `/questions/${encodeURIComponent(question?.id)}`,
      window.location.href
    )
    navigator.clipboard.writeText(url.toString())
    toast.success(intl.formatMessage({ id: 'Question link copied' }))
  }

  function handleTextareaFocus(event) {
    event.preventDefault()
    setIsFocused(true)
    textareaRef.current.focus()
  }

  async function handleSubmit(event) {
    event.preventDefault()
    try {
      await postReply({
        data: { reply },
      })
      fetchQuestions()
      setIsEditing(false)
    } catch (err) {
      if (err?.response?.status === 404) return setNotFoundModal(true)
      toast.error(
        intl.formatMessage({
          id: err?.response?.data?.message || 'Error while answering question.',
        })
      )
      console.error(err)
    }
  }

  function handleEdit(event) {
    event.preventDefault()
    setIsEditing(true)
    setReply(merchantAnswer?.text || '')
  }

  function handleCloseModal(event) {
    event.preventDefault()
    setModal(false)
    setIsEditing(false)
    setReply('')
  }

  function handleCloseNotFound(event) {
    event.preventDefault()
    setNotFoundModal(false)
    fetchQuestions()
  }

  React.useEffect(() => {
    if (!question) return

    const merchantAnswer = question?.answers?.filter(
      answer =>
        answer?.author?.type === 'MERCHANT' ||
        answer?.author?.type === 'TEMPORARY'
    )?.[0]
    const userAnswers = question?.answers?.filter(
      answer =>
        answer?.author?.type !== 'MERCHANT' &&
        answer?.author?.type !== 'TEMPORARY'
    )
    setMerchantAnswer(merchantAnswer || null)
    setUserAnswers(userAnswers || [])
  }, [question])

  return (
    <CustomCard>
      <StyledModal
        isOpen={modal}
        toggle={() => setModal(prevState => !prevState)}
        backdrop="static"
        size="xl"
      >
        <StyledModalBody>
          <div className="modal-menu">
            <button onClick={handleCloseModal}>
              <ArrowLeft size="15" color="#2F2B3DB2" />
            </button>
            <div>
              <FormattedMessage id="Question" />
            </div>
          </div>
          <div className="info-row">
            <div>{question?.author?.displayName}</div>
          </div>
          <div className="location-row">
            <MapPin size={15} color="#2F2B3DB2" min={15} />
            <div
              data-show={addressShow}
              onClick={() => setAddressShow(prevState => !prevState)}
            >
              {`${question?.location?.data?.storefrontAddress?.addressLines?.[0]} - ${question?.location?.data?.storefrontAddress?.locality} - ${question?.location?.data?.storefrontAddress?.administrativeArea}`}
            </div>
          </div>
          <div className="thread-row">
            {question?.text && (
              <div>
                <img
                  alt=""
                  width="32"
                  height="32"
                  src={question?.author?.profilePhotoUri}
                ></img>
                <div className="comment-div">
                  <div>
                    <div>
                      <FormattedDate
                        format="dd/mm/yyyy"
                        value={question?.data?.updateTime}
                      />{' '}
                      <FormattedTime
                        format="hh:mm"
                        value={question?.data?.updateTime}
                      />
                    </div>
                    <div>{question?.author?.displayName}</div>
                  </div>

                  {question?.text && (
                    <div>
                      {question.text.replace(REVIEW_TRANSLATION_REGEX, '')}
                    </div>
                  )}
                </div>
              </div>
            )}
            {!isEditing && (
              <>
                {merchantAnswer && (
                  <div className="review-reply-div">
                    <div>
                      <div>
                        <FormattedDate
                          format="dd/mm/yyyy"
                          value={merchantAnswer?.data?.updateTime}
                        />{' '}
                        <FormattedTime
                          format="hh:mm"
                          value={merchantAnswer?.data?.updateTime}
                        />
                      </div>
                      {merchantAnswer?.repliedBy && (
                        <div>{merchantAnswer?.repliedBy?.name}</div>
                      )}
                    </div>
                    {merchantAnswer?.text && (
                      <div>
                        {merchantAnswer.text.replace(
                          REVIEW_TRANSLATION_REGEX,
                          ''
                        )}
                        <button
                          className="edit-reply-button"
                          onClick={handleEdit}
                        >
                          <FormattedMessage id="Edit" />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
            {userAnswers
              ?.sort(
                (a, b) =>
                  new Date(a?.data?.updateTime).getTime() -
                  new Date(b?.data?.updateTime).getTime()
              )
              ?.map(answer => (
                <div key={answer.id} className="review-reply-div">
                  <div>
                    <div>
                      <FormattedDate
                        format="dd/mm/yyyy"
                        value={answer?.data?.updateTime}
                      />{' '}
                      <FormattedTime
                        format="hh:mm"
                        value={answer?.data?.updateTime}
                      />
                    </div>
                    <div>{answer?.author?.displayName}</div>
                  </div>
                  {answer?.text && (
                    <div>
                      {answer.text.replace(REVIEW_TRANSLATION_REGEX, '')}
                    </div>
                  )}
                </div>
              ))}
          </div>
          {isEditing && (
            <div className="editing-row">
              <div className="reply-div">
                <textarea
                  rows="4"
                  placeholder={intl.formatMessage({ id: 'Enter a reply...' })}
                  disabled={loading}
                  value={reply}
                  onChange={e => setReply(e.target.value)}
                />
              </div>
              <div>
                <button
                  className="reply-button"
                  disabled={loading || !reply?.length}
                  onClick={handleSubmit}
                >
                  <FormattedMessage id="Save" />
                </button>
              </div>
            </div>
          )}
        </StyledModalBody>
      </StyledModal>

      <Modal
        isOpen={notFoundModal}
        toggle={setNotFoundModal}
        centered={true}
        size="lg"
      >
        <NotFoundModal>
          <div className="notfound-icon">
            <AlertTriangle size={70} color="#fff" />
          </div>
          <div className="notfound-text">
            <div>
              <div className="notfound-error">
                <FormattedMessage id="ERROR 404" />
              </div>
              <div className="notfound-title">
                <FormattedMessage id="It wasn't possible to answer the question" />
              </div>
              <div className="notfound-description">
                <FormattedMessage id="This question was deleted by the user or Google" />
              </div>
            </div>

            <button onClick={handleCloseNotFound}>
              <FormattedMessage id="Close" />
            </button>
          </div>
        </NotFoundModal>
      </Modal>

      <div className="first-row">
        <div className="question-tag">
          <FormattedMessage id="Question" />
        </div>
        <div className="row-menu">
          <div>
            <FormattedDate
              format="dd/mm/yyyy"
              value={question?.data?.updateTime}
            />{' '}
            (
            <FormattedTime format="hh:mm" value={question?.data?.updateTime} />)
          </div>
          <StyledDropdown>
            <DropdownToggle caret data-type="kebab">
              <MoreVertical size="15" color="#2F2B3DB2" />
            </DropdownToggle>
            <DropdownMenu>
              <DropdownItem onClick={handleCopyToClipboard}>
                <div>
                  <Link size="15" color="#C4C4C4" />
                </div>
                <FormattedMessage id="Copy question link" />
              </DropdownItem>
              <DropdownItem
                onClick={() => setModal(true)}
                disabled={!merchantAnswer}
              >
                <div>
                  <Edit size="15" color="#C4C4C4" />
                </div>
                <FormattedMessage id="Edit reply" />
              </DropdownItem>
            </DropdownMenu>
          </StyledDropdown>
        </div>
      </div>
      <div className="location-row">
        <MapPin size={15} color="#2F2B3DB2" min={15} />
        <div
          data-show={addressShow}
          onClick={() => setAddressShow(prevState => !prevState)}
        >
          {`${question?.location?.data?.storefrontAddress?.addressLines?.[0]} - ${question?.location?.data?.storefrontAddress?.locality} - ${question?.location?.data?.storefrontAddress?.administrativeArea}`}
        </div>
      </div>
      <div className="comment-row">
        <div className="reviewer-div">
          <img
            alt=""
            width="32"
            height="32"
            src={question?.author?.profilePhotoUri}
          ></img>
          <div>{question?.author?.displayName}</div>
        </div>
        {question?.text && (
          <div className="comment-div">
            <div>
              <FormattedDate
                format="dd/mm/yyyy"
                value={question?.data?.updateTime}
              />{' '}
              (
              <FormattedTime
                format="hh:mm"
                value={question?.data?.updateTime}
              />
              )
            </div>
            <div>{question.text.replace(REVIEW_TRANSLATION_REGEX, '')}</div>
          </div>
        )}
        {merchantAnswer ? (
          <>
            <div className="review-reply-div">
              <div>
                <div>
                  <FormattedDate
                    format="dd/mm/yyyy"
                    value={merchantAnswer?.data?.updateTime}
                  />{' '}
                  <FormattedTime
                    format="hh:mm"
                    value={merchantAnswer?.data?.updateTime}
                  />
                </div>
                {merchantAnswer?.repliedBy && (
                  <div>{merchantAnswer?.repliedBy?.name}</div>
                )}
              </div>
              {merchantAnswer?.text && (
                <div>
                  {merchantAnswer.text.replace(REVIEW_TRANSLATION_REGEX, '')}
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            {!isFocused && (
              <div className="reply-div">
                <textarea
                  rows="1"
                  placeholder={intl.formatMessage({ id: 'Enter a reply...' })}
                  disabled={loading}
                  value={reply}
                  onChange={e => setReply(e.target.value)}
                  onFocus={handleTextareaFocus}
                />
              </div>
            )}
            <div
              className="reply-div"
              style={{
                opacity: isFocused ? '100%' : '0',
                height: isFocused ? 'auto' : '0',
                pointerEvents: isFocused ? 'auto' : 'none',
              }}
            >
              <Textarea
                ref={textareaRef}
                rows="4"
                placeholder={intl.formatMessage({ id: 'Enter a reply...' })}
                disabled={loading}
                setValue={v => setReply(v)}
                enableVariables
                value={reply}
                onChange={e => setReply(e.target.value)}
              />
            </div>
            {isFocused && (
              <>
                <div className="reply-menu">
                  <div className="template-and-reply">
                    <Select
                      options={variableOptions}
                      style={{ minWidth: '200px !important' }}
                      className="mr-25"
                      onChange={({ value }) => setReply(value)}
                      isDisabled={loading}
                      placeholder="Templates"
                      noOptionsMessage={() => (
                        <Link to="/templates/questions">
                          <FormattedMessage id="No template have been created yet. Click and create one." />
                        </Link>
                      )}
                    />
                    <button
                      className="reply-button"
                      disabled={loading || !reply?.length}
                      onClick={handleSubmit}
                    >
                      <FormattedMessage id="Reply" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </>
        )}
        {Boolean(userAnswers.length) && (
          <button
            className="user-replies-button"
            onClick={() => setModal(true)}
          >
            <MessageSquare size={24} color="#484848" />
            <div>
              {userAnswers.length} <FormattedMessage id="user replies" />
            </div>
          </button>
        )}
      </div>
    </CustomCard>
  )
}

const StyledModal = styled(Modal)`
  .modal-content {
    height: calc(100dvh - 3.5rem);

    @media ${queries.less.sm} {
      height: calc(100dvh - 1rem);
    }
  }
`

const StyledModalBody = styled(ModalBody)`
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: start;
  padding: 0;

  button:disabled {
    cursor: not-allowed;
    opacity: 0.3;
  }

  > div {
    width: 100%;
  }

  .modal-menu {
    display: flex;
    flex-direction: row;
    justify-content: start;
    align-items: center;
    gap: 1rem;
    padding: 1rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.15);
    color: #2f2b3db2;
    font-size: 1rem;
    font-weight: 600;

    button {
      display: flex;
      justify-content: center;
      align-items: center;
      border-radius: 99999px;
      border: 1px solid #d1d1d1;
      padding: 0.5rem;
      background-color: transparent;
    }
  }

  .info-row {
    display: flex;
    flex-direction: column;
    justify-content: start;
    align-items: start;
    gap: 0.5rem;
    padding: 1rem;
    color: #2f2b3de5;
    font-size: 1rem;
    font-weight: 600;
    border-bottom: 1px solid rgba(0, 0, 0, 0.15);

    .question-tag {
      display: flex;
      justify-content: start;
      align-items: center;
      padding: 0.5rem 1rem;
      background-color: #7367f029;
      color: #7367f0;
      border-radius: 0.25rem;
    }
  }

  .location-row {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: start;
    align-items: center;
    gap: 0.5rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.15);
    color: #979797;
    padding: 1rem;

    > div {
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      text-overflow: ellipsis;
      overflow: hidden;

      &[data-show='true'] {
        -webkit-line-clamp: unset;
        overflow: unset;
      }
    }

    svg {
      min-width: 15px;
    }
  }

  .thread-row {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: start;
    padding: 1rem;
    gap: 1rem;

    > div {
      display: flex;
      flex-direction: row;
      justify-content: start;
      align-items: center;
      gap: 0.5rem;
    }

    .comment-div {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: start;
      gap: 0.5rem;
      background-color: #f8f7fa;
      border-radius: 0 8px 8px 8px;
      padding: 0.7rem;
      color: #2f2b3de5;
      font-weight: 400;
      font-size: 0.9rem;

      div:nth-child(1) {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 1rem;
        width: 100%;
        color: #2f2b3db2;
        font-size: 0.8rem;

        > div {
          min-width: fit-content;
        }
      }
    }

    .review-reply-div {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: start;
      gap: 0.5rem;
      background-color: #fff;
      border: 1px solid #f8f7fa;
      border-radius: 8px 0 8px 8px;
      padding: 0.7rem;
      color: #2f2b3de5;
      font-weight: 400;
      font-size: 0.9rem;
      margin-left: 2.7rem;
      align-self: flex-end;

      .edit-reply-button {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 0.5rem 1rem 0 0;
        background-color: #fff;
        border-radius: 0.25rem;
        color: #857bf2;
        border: none;
        font-weight: 600;
      }

      div:nth-child(1) {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        color: #2f2b3db2;
        font-size: 0.8rem;
        gap: 1rem;

        > div {
          min-width: fit-content;
        }
      }
    }
  }

  .editing-row {
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 1rem;
    gap: 0.5rem;
    border-top: 1px solid rgba(0, 0, 0, 0.15);
    margin-top: auto;

    > div:nth-child(2) {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: 0.5rem;
    }

    .ai-generate {
      width: 100%;
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 0.5rem 5rem 0.5rem 1.25rem;
      background-color: #f7fafc;
      color: #718096;
      border: 1px solid #e2e8f0;
      border-radius: 0.25rem;
      font-weight: 600;

      > div {
        position: absolute;
        right: 0.75rem;
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 0.25rem;
        background-color: #718096;
        color: #fff;
        padding: 0.125rem 0.375rem;
        border-radius: 0.125rem;
      }
    }

    .reply-button {
      width: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 0.5rem 1.25rem;
      background-color: #7367f0;
      border-radius: 0.25rem;
      border: none;
      color: #fff;
    }

    .reply-div {
      width: 100%;
      position: relative;

      textarea {
        width: 100% !important;
        min-height: 2.5rem !important;
        border: 1px solid #d1d1d1 !important;
        border-radius: 0.375rem !important;
        padding: 0.4rem 0.9rem !important;
        font-size: 1rem !important;
        line-height: 1.6rem !important;
        color: #2f2b3db2 !important;
        resize: none !important;

        &::placeholder {
          color: #2f2b3db2 !important;
          font-size: 1rem !important;
        }
      }
    }
  }
`

const CustomCard = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: #fff;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 0.5rem;

  > div {
    padding: 1rem;
  }

  .first-row {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid rgba(0, 0, 0, 0.15);

    .question-tag {
      display: flex;
      justify-content: start;
      align-items: center;
      padding: 0.5rem 1rem;
      background-color: #7367f029;
      color: #7367f0;
      border-radius: 0.25rem;
    }

    .row-menu {
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      gap: 1rem;

      > div:nth-child(1) {
        color: #2f2b3db2;
        font-size: 0.8rem;
      }
    }
  }

  .location-row {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: start;
    align-items: center;
    gap: 0.5rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.15);
    color: #979797;

    > div {
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      text-overflow: ellipsis;
      overflow: hidden;

      &[data-show='true'] {
        -webkit-line-clamp: unset;
        overflow: unset;
      }
    }

    svg {
      min-width: 15px;
    }
  }

  .comment-row {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: start;
    gap: 0.5rem;

    .user-replies-button {
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 0;
      background-color: transparent;
      gap: 0.5rem;
      color: #857bf2;
      border: none;
    }

    .reviewer-div {
      display: flex;
      justify-content: start;
      align-items: center;
      gap: 0.5rem;

      color: #2f2b3de5;
      font-size: 1rem;
      font-weight: 600;
    }

    .comment-div {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: start;
      gap: 0.5rem;
      background-color: #f8f7fa;
      border-radius: 0 8px 8px 8px;
      padding: 0.7rem;
      color: #2f2b3de5;
      font-weight: 400;
      font-size: 0.9rem;

      div:nth-child(1) {
        color: #2f2b3db2;
        font-size: 0.8rem;
      }
    }

    .review-reply-div {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: start;
      gap: 0.5rem;
      background-color: #fff;
      border: 1px solid #f8f7fa;
      border-radius: 8px 0 8px 8px;
      padding: 0.7rem;
      color: #2f2b3de5;
      font-weight: 400;
      font-size: 0.9rem;
      margin-left: 1.5rem;
      align-self: flex-end;

      div:nth-child(1) {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        color: #2f2b3db2;
        font-size: 0.8rem;
        gap: 1rem;

        > div {
          min-width: fit-content;
        }
      }

      div:nth-child(2) {
        @media ${queries.less.sm} {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          text-overflow: ellipsis;
          overflow: hidden;
        }
      }
    }

    .reply-div {
      width: 100%;
      position: relative;

      textarea {
        width: 100% !important;
        min-height: 2.5rem !important;
        border: 1px solid #d1d1d1 !important;
        border-radius: 0.375rem !important;
        padding: 0.4rem 0.9rem !important;
        font-size: 1rem !important;
        line-height: 1.6rem !important;
        color: #2f2b3db2 !important;
        resize: none !important;

        &::placeholder {
          color: #2f2b3db2 !important;
          font-size: 1rem !important;
        }
      }

      .ia-tag {
        height: 1.5rem;
        pointer-events: none;
        position: absolute;
        right: 1rem;
        top: 0.5rem;
        bottom: 0.5rem;
        background-color: #718096;
        border-radius: 0.125rem;
        padding: 0.125rem 0.375rem;
        color: #fff;
      }
    }
  }

  .reply-menu {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 0.5rem;

    button:disabled {
      cursor: not-allowed;
      opacity: 0.3;
    }

    @media ${queries.less.sm} {
      flex-direction: column;
      gap: 0.5rem;

      .ai-generate {
        width: 100%;
      }

      .reply-button {
        width: 100%;
      }

      > div {
        width: 100%;
      }
    }

    .template-and-reply {
      display: flex;
      flex-direction: row;
      justify-content: end;
      align-items: center;
      gap: 0.5rem;
      margin-left: auto;

      @media ${queries.less.sm} {
        flex-direction: column;

        > div {
          width: 100%;
        }
      }
    }

    .ai-generate {
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 0.5rem 5rem 0.5rem 1.25rem;
      background-color: #f7fafc;
      color: #718096;
      border: 1px solid #e2e8f0;
      border-radius: 0.25rem;
      font-weight: 600;

      > div {
        position: absolute;
        right: 0.75rem;
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 0.25rem;
        background-color: #718096;
        color: #fff;
        padding: 0.125rem 0.375rem;
        border-radius: 0.125rem;
      }
    }

    .reply-button {
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 0.5rem 1.25rem;
      background-color: #7367f0;
      border-radius: 0.25rem;
      border: none;
      color: #fff;
    }
  }
`

const StyledDropdown = styled(UncontrolledButtonDropdown)`
  .dropdown-toggle {
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    background-color: #f7f7f7 !important;
    border: none !important;
    padding: 0.5rem !important;

    svg {
      position: auto !important;
      left: 0 !important;
    }

    &[data-type='kebab'] {
      background-color: #f7f7f7 !important;
    }
  }

  .dropdown-item {
    justify-content: start !important;
    gap: 0.5rem;

    &:hover {
      background-color: #deebff !important;
      color: #2a2e30 !important;
    }

    > div {
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: #f7f7f7;
      border-radius: 0.25rem;
      padding: 0.5rem;
    }
  }
`

const NotFoundModal = styled.div`
  height: 20rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: inherit;
  overflow: hidden;

  > .notfound-icon {
    height: 100%;
    width: 35%;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 2rem 2rem;
    background-color: #ed8936;
  }

  > .notfound-text {
    height: 100%;
    width: 65%;
    display: flex;
    flex-direction: column;
    padding: 2rem 2rem;

    > div {
      height: 100%;
      display: flex;
      flex-direction: column;
      gap: 1.5em;
    }

    .notfound-error {
      font-size: 0.9rem;
      font-weight: 600;
      color: #a0aec0;
    }

    .notfound-title {
      font-size: 1.4rem;
      font-weight: 400;
      color: #1a202c;

      @media ${queries.less.sm} {
        font-size: 1rem;
      }
    }

    .notfound-description {
      font-size: 1.2rem;
      font-weight: 400;
      color: #1a202c;

      @media ${queries.less.sm} {
        font-size: 0.9rem;
      }
    }

    button {
      width: fit-content;
      background-color: transparent;
      color: #718096;
      padding: 1rem 1.5rem 1rem 0;
      border: none;
      margin: 0;
      font-size: 1.1rem;
      font-weight: 600;

      @media ${queries.less.sm} {
        width: 100%;
        padding: 1rem 1.5rem;
      }
    }
  }
`

export default QuestionCard
